import React, { useState, useEffect } from 'react';
import { 
  Box, 
  Stepper, 
  Step, 
  StepLabel as MuiStepLabel, 
  // StepContent as MuiStepContent, 
  Button, 
  Typography, 
  Paper,
  useTheme,
  IconButton,
  Tooltip
} from '@mui/material';
import { styled } from '@mui/material/styles';
import StepConnector, { stepConnectorClasses } from '@mui/material/StepConnector';
import { StepIconProps } from '@mui/material/StepIcon';
import { motion } from 'framer-motion';
import { 
  Check, 
  ArrowForward, 
  ArrowBack, 
  Refresh,
  // Info as InfoIcon
} from '@mui/icons-material';


// Define the step interface
export interface StepData {
  label: string;
  description?: string;
  icon?: React.ReactNode;
  content?: React.ReactNode;
  optional?: boolean;
  cost?: number;
}

interface HolographicStepperProps {
  steps: StepData[];
  orientation?: 'horizontal' | 'vertical';
  variant?: 'text' | 'dots' | 'progress';
  initialStep?: number;
  onChange?: (step: number) => void;
  onComplete?: () => void;
  showContent?: boolean;
  alternativeLabel?: boolean;
  onSubmitRFP?: () => void;
}

// Custom connector with holographic styling
const HolographicConnector = styled(StepConnector)(({ theme }) => ({
  [`&.${stepConnectorClasses.alternativeLabel}`]: {
    top: 22,
  },
  [`&.${stepConnectorClasses.active}`]: {
    [`& .${stepConnectorClasses.line}`]: {
      backgroundImage: 'linear-gradient(90deg, #003366, #00CC99, #8A2BE2, #FF6F00)',
      backgroundSize: '300% 100%',
      animation: 'gradient 6s linear infinite',
    },
  },
  [`&.${stepConnectorClasses.completed}`]: {
    [`& .${stepConnectorClasses.line}`]: {
      backgroundImage: 'linear-gradient(90deg, #00CC99, #8A2BE2)',
    },
  },
  [`& .${stepConnectorClasses.line}`]: {
    height: 3,
    border: 0,
    backgroundColor: 'rgba(138, 43, 226, 0.2)',
    borderRadius: 1,
    transition: 'all 0.3s ease',
  },
  '@keyframes gradient': {
    '0%': {
      backgroundPosition: '0% 50%'
    },
    '50%': {
      backgroundPosition: '100% 50%'
    },
    '100%': {
      backgroundPosition: '0% 50%'
    }
  }
}));

// Custom step icon with holographic styling
const HolographicStepIconRoot = styled('div')<{
  ownerState: { completed?: boolean; active?: boolean };
}>(({ theme, ownerState }) => ({
  backgroundColor: 'rgba(26, 26, 46, 0.7)',
  zIndex: 1,
  color: '#fff',
  width: 50,
  height: 50,
  display: 'flex',
  borderRadius: '50%',
  justifyContent: 'center',
  alignItems: 'center',
  border: '1px solid rgba(138, 43, 226, 0.3)',
  backdropFilter: 'blur(10px)',
  boxShadow: '0 4px 10px rgba(0, 0, 0, 0.2)',
  transition: 'all 0.3s ease',
  position: 'relative',
  overflow: 'hidden',
  '&::before': {
    content: '""',
    position: 'absolute',
    top: '-50%',
    left: '-50%',
    width: '200%',
    height: '200%',
    background: 'linear-gradient(0deg, transparent, transparent 30%, rgba(0, 255, 255, 0.3))',
    transform: 'rotate(-45deg)',
    transition: 'all 0.5s ease',
    opacity: 0,
  },
  '&:hover': {
    '&::before': {
      opacity: 1,
      transform: 'rotate(-45deg) translateY(100%)',
    },
  },
  ...(ownerState.active && {
    backgroundImage: 'linear-gradient(45deg, #003366 30%, #8A2BE2 90%)',
    boxShadow: '0 0 15px rgba(138, 43, 226, 0.5)',
    '&::after': {
      content: '""',
      position: 'absolute',
      inset: 0,
      borderRadius: '50%',
      padding: 2,
      background: 'linear-gradient(45deg, #00CC99, #8A2BE2)',
      WebkitMask: 'linear-gradient(#fff 0 0) content-box, linear-gradient(#fff 0 0)',
      WebkitMaskComposite: 'xor',
      maskComposite: 'exclude',
    },
  }),
  ...(ownerState.completed && {
    backgroundImage: 'linear-gradient(45deg, #00CC99 30%, #00ffff 90%)',
  }),
}));

// Custom step icon component
function HolographicStepIcon(props: StepIconProps & { customIcon?: React.ReactNode }) {
  const { active, completed, className, customIcon } = props;

  return (
    <HolographicStepIconRoot ownerState={{ completed, active }} className={className}>
      {completed ? (
        <Check sx={{ fontSize: 24 }} />
      ) : active ? (
        customIcon || <motion.div 
          animate={{ 
            scale: [1, 1.1, 1],
            opacity: [0.8, 1, 0.8]
          }}
          transition={{ 
            duration: 2,
            repeat: Infinity,
            ease: "easeInOut"
          }}
        >
          {String(props.icon)}
        </motion.div>
      ) : (
        customIcon || String(props.icon)
      )}
    </HolographicStepIconRoot>
  );
}

// Styled components for the stepper
const StepperContainer = styled(Box)(({ theme }) => ({
  position: 'relative',
  padding: theme.spacing(5),
  background: 'rgba(26, 26, 46, 0.6)',
  backdropFilter: 'blur(10px)',
  borderRadius: theme.spacing(2),
  border: '1px solid rgba(138, 43, 226, 0.2)',
  overflow: 'hidden',
  '&::before': {
    content: '""',
    position: 'absolute',
    top: '-50%',
    left: '-50%',
    width: '200%',
    height: '200%',
    background: 'linear-gradient(0deg, transparent, transparent 30%, rgba(0, 255, 255, 0.3))',
    opacity: 0,
  },
  '&::after': {
    content: '""',
    position: 'absolute',
    bottom: 0,
    left: 0,
    right: 0,
    height: '2px',
    background: 'linear-gradient(90deg, #003366, #00CC99, #8A2BE2, #FF6F00)',
    backgroundSize: '300% 100%',
    animation: 'gradient 6s linear infinite',
  },
  '&:hover': {
    boxShadow: '0 0 20px rgba(138, 43, 226, 0.3)',
    '&::before': {
      opacity: 1,
      transform: 'rotate(-45deg) translateY(100%)',
    },
  },
  '@keyframes gradient': {
    '0%': {
      backgroundPosition: '0% 50%'
    },
    '50%': {
      backgroundPosition: '100% 50%'
    },
    '100%': {
      backgroundPosition: '0% 50%'
    }
  }
}));

const ContentBox = styled(Box)(({ theme }) => ({
  padding: theme.spacing(2),
  marginTop: theme.spacing(2),
  background: 'rgba(0, 51, 102, 0.3)',
  borderRadius: theme.spacing(1),
  border: '1px solid rgba(0, 204, 153, 0.3)',
  position: 'relative',
  overflow: 'hidden',
  transition: 'all 0.3s ease',
  '&::before': {
    content: '""',
    position: 'absolute',
    top: '-50%',
    left: '-50%',
    width: '200%',
    height: '200%',
    background: 'linear-gradient(0deg, transparent, transparent 30%, rgba(0, 204, 153, 0.2))',
    transform: 'rotate(-45deg)',
    transition: 'all 0.5s ease',
    opacity: 0,
  },
  '&:hover': {
    boxShadow: '0 5px 15px rgba(0, 204, 153, 0.2)',
    '&::before': {
      opacity: 1,
      transform: 'rotate(-45deg) translateY(100%)',
    },
  },
}));

const ActionButton = styled(Button)(({ theme }) => ({
  margin: theme.spacing(1, 1, 0, 0),
  position: 'relative',
  overflow: 'hidden',
  '&::before': {
    content: '""',
    position: 'absolute',
    top: 0,
    left: '-100%',
    width: '100%',
    height: '100%',
    background: 'linear-gradient(90deg, transparent, rgba(255, 255, 255, 0.2), transparent)',
    transition: 'all 0.5s ease',
  },
  '&:hover::before': {
    left: '100%',
  },
}));

const NextButton = styled(ActionButton)(({ theme }) => ({
  background: 'linear-gradient(45deg, #8A2BE2 30%, #9370DB 90%)',
  color: '#fff',
  '&:hover': {
    background: 'linear-gradient(45deg, #9370DB 30%, #8A2BE2 90%)',
    boxShadow: '0 0 15px rgba(138, 43, 226, 0.5)',
  },
}));

const BackButton = styled(ActionButton)(({ theme }) => ({
  borderColor: 'rgba(138, 43, 226, 0.5)',
  color: '#fff',
  '&:hover': {
    borderColor: '#8A2BE2',
    backgroundColor: 'rgba(138, 43, 226, 0.1)',
  },
}));

const ResetButton = styled(ActionButton)(({ theme }) => ({
  borderColor: 'rgba(0, 204, 153, 0.5)',
  color: '#00CC99',
  '&:hover': {
    borderColor: '#00CC99',
    backgroundColor: 'rgba(0, 204, 153, 0.1)',
  },
}));

const StepLabelText = styled(Typography)(({ theme }) => ({
  color: '#fff',
  fontWeight: 600,
}));

const StepDescription = styled(Typography)(({ theme }) => ({
  color: 'rgba(255, 255, 255, 0.7)',
}));

// Add these styles to ensure content is visible
//const StepContent = styled(Box)(({ theme }) => ({
  //padding: theme.spacing(3),
  //marginTop: theme.spacing(2),
  //background: 'rgba(26, 26, 46, 0.4)',
  //borderRadius: theme.spacing(1),
  //border: '1px solid rgba(138, 43, 226, 0.2)',
//}));

// Main component
const HolographicStepper: React.FC<HolographicStepperProps> = ({
  steps,
  orientation = 'horizontal',
  variant = 'text',
  initialStep = 0,
  onChange,
  onComplete,
  showContent = true,
  alternativeLabel = false,
  onSubmitRFP,
}) => {
  const [activeStep, setActiveStep] = useState(initialStep);
  const [completed, setCompleted] = useState<{ [k: number]: boolean }>({});
  const [stepCosts, setStepCosts] = useState<{ [k: number]: number }>(
    steps.reduce((acc, step, index) => ({ ...acc, [index]: step.cost || 0 }), {})
  );
  const theme = useTheme();

  const totalSteps = () => {
    return steps.length;
  };

  const completedSteps = () => {
    return Object.keys(completed).length;
  };

  const isLastStep = () => {
    return activeStep === totalSteps() - 1;
  };

  const allStepsCompleted = () => {
    return completedSteps() === totalSteps();
  };

  const calculateTotalCost = () => {
    return Object.values(stepCosts).reduce((sum, cost) => sum + cost, 0);
  };

  const updateStepCost = (stepIndex: number, newCost: number) => {
    setStepCosts(prevCosts => ({ ...prevCosts, [stepIndex]: newCost }));
    
    steps[stepIndex].cost = newCost;
  };

  const getStepContent = (step: StepData, index: number) => {
    if (React.isValidElement(step.content)) {
      return React.cloneElement(step.content as React.ReactElement<any>, {
        onCostChange: (newCost: number) => updateStepCost(index, newCost),
        currentCost: stepCosts[index],
      });
    }
    return step.content;
  };

  const handleNext = () => {
    if (activeStep < steps.length - 1) {
      setActiveStep((prevActiveStep) => prevActiveStep + 1);
    } else {
      if (onComplete) {
        onComplete();
      }
    }
  };

  const handleBack = () => {
    setActiveStep((prevActiveStep) => prevActiveStep - 1);
    if (onChange) {
      onChange(activeStep - 1);
    }
  };

  const handleStepChange = (step: number) => {
    setActiveStep(step);
    if (onChange) {
      onChange(step);
    }
  };

  const handleComplete = () => {
    const newCompleted = completed;
    newCompleted[activeStep] = true;
    setCompleted(newCompleted);
    
    if (completedSteps() === totalSteps()) {
      onComplete && onComplete();
    } else {
      handleNext();
    }
  };

  const handleAddToProposal = () => {
    const newCompleted = { ...completed };
    newCompleted[activeStep] = true;
    setCompleted(newCompleted);
    
    if (onSubmitRFP) {
      onSubmitRFP();
    }
    
    if (onComplete) {
      onComplete();
    }
  };

  const handleReset = () => {
    setActiveStep(0);
    setCompleted({});
    setStepCosts(steps.reduce((acc, step, index) => ({ ...acc, [index]: step.cost || 0 }), {}));
    if (onChange) {
      onChange(0);
    }
  };

  useEffect(() => {
    const newStepCosts = steps.reduce((acc, step, index) => {
      return { ...acc, [index]: step.cost !== undefined ? step.cost : (stepCosts[index] || 0) };
    }, {});
    setStepCosts(newStepCosts);
  }, [steps]);

  useEffect(() => {
    steps.forEach((step, index) => {
      if (step.cost !== undefined && step.cost !== stepCosts[index]) {
        setStepCosts(prevCosts => ({ ...prevCosts, [index]: step.cost || 0 }));
      }
    });
  }, [steps, stepCosts]);

  return (
    <StepperContainer>
      <Stepper 
        activeStep={activeStep} 
        orientation={orientation}
        alternativeLabel={alternativeLabel}
        connector={<HolographicConnector />}
      >
        {steps.map((step, index) => {
          const stepProps: { completed?: boolean } = {};
          const labelProps: { optional?: React.ReactNode } = {};
          
          if (step.optional) {
            labelProps.optional = (
              <Typography variant="caption" sx={{ color: 'rgba(255, 255, 255, 0.5)' }}>
                Optional
              </Typography>
            );
          }
          
          if (completed[index]) {
            stepProps.completed = true;
          }
          
          return (
            <Step key={step.label} {...stepProps}>
              <MuiStepLabel 
                StepIconComponent={(iconProps) => 
                  <HolographicStepIcon {...iconProps} customIcon={step.icon} />
                }
                {...labelProps}
                onClick={() => handleStepChange(index)}
                sx={{ cursor: 'pointer' }}
              >
                <StepLabelText variant="subtitle1">{step.label}</StepLabelText>
                {step.description && (
                  <Tooltip title={step.description}>
                    <StepDescription variant="body2" sx={{ 
                      display: orientation === 'vertical' ? 'block' : 'none',
                      color: 'rgba(255, 255, 255, 0.7)'
                    }}>
                      {step.description}
                    </StepDescription>
                  </Tooltip>
                )}
              </MuiStepLabel>
              
              {orientation === 'vertical' && showContent && activeStep === index && (
                <Box sx={{ ml: 4, mt: 2, mb: 3 }}>
                  {step.content || (
                    <ContentBox>
                      <Typography>{step.description}</Typography>
                      <Box sx={{ mb: 2 }}>
                        <div>
                          <NextButton
                            variant="contained"
                            onClick={isLastStep() ? handleComplete : handleNext}
                            sx={{ mt: 1, mr: 1 }}
                            endIcon={isLastStep() ? <Check /> : <ArrowForward />}
                          >
                            {isLastStep() ? 'Finish' : 'Continue'}
                          </NextButton>
                          <BackButton
                            disabled={activeStep === 0}
                            onClick={handleBack}
                            sx={{ mt: 1, mr: 1 }}
                            variant="outlined"
                            startIcon={<ArrowBack />}
                          >
                            Back
                          </BackButton>
                        </div>
                      </Box>
                    </ContentBox>
                  )}
                </Box>
              )}
            </Step>
          );
        })}
      </Stepper>
      
      {orientation === 'horizontal' && showContent && activeStep < steps.length && (
        <motion.div
          key={activeStep}
          initial={{ opacity: 0, y: 10 }}
          animate={{ opacity: 1, y: 0 }}
          exit={{ opacity: 0, y: -10 }}
          transition={{ duration: 0.3 }}
        >
          <ContentBox sx={{ mt: 3 }}>
            {getStepContent(steps[activeStep], activeStep) || (
              <Typography>{steps[activeStep].description}</Typography>
            )}
          </ContentBox>
        </motion.div>
      )}
      
      {showContent && (
        <Box sx={{ display: 'flex', flexDirection: 'row', pt: 2 }}>
          <BackButton
            variant="outlined"
            disabled={activeStep === 0}
            onClick={handleBack}
            startIcon={<ArrowBack />}
          >
            Back
          </BackButton>
          <Box sx={{ flex: '1 1 auto' }} />
          
          {activeStep !== steps.length && (
            <Box sx={{ mr: 2, display: 'flex', alignItems: 'center' }}>
              <Typography variant="body2" sx={{ color: '#00CC99', mr: 1 }}>
                Step Cost:
              </Typography>
              <Typography variant="body1" sx={{ color: '#00CC99', fontWeight: 600 }}>
                ${stepCosts[activeStep].toLocaleString()}
              </Typography>
            </Box>
          )}
          
          {activeStep !== steps.length && (
            <Tooltip title={isLastStep() ? "Add these tools to your proposal" : "Go to next step"}>
              <NextButton
                variant="contained"
                onClick={isLastStep() ? handleAddToProposal : handleNext}
                endIcon={isLastStep() ? <Check /> : <ArrowForward />}
              >
                {isLastStep() ? 'Add to Proposal' : 'Next'}
              </NextButton>
            </Tooltip>
          )}
          
          {activeStep !== steps.length && (
            <Tooltip title="Reset progress">
              <IconButton 
                onClick={handleReset}
                sx={{ ml: 1, color: 'rgba(0, 204, 153, 0.7)' }}
              >
                <Refresh />
              </IconButton>
            </Tooltip>
          )}
        </Box>
      )}
      
      <Box 
        sx={{ 
          display: 'flex', 
          justifyContent: 'flex-end', 
          alignItems: 'center',
          mt: 2, 
          p: 1,
          borderTop: '1px solid rgba(0, 204, 153, 0.3)',
        }}
      >
        <Typography variant="body2" sx={{ color: '#00CC99', mr: 1 }}>
          Running Total:
        </Typography>
        <Typography variant="h6" sx={{ color: '#00CC99', fontWeight: 600 }}>
          ${calculateTotalCost().toLocaleString()}
        </Typography>
      </Box>
      
      {allStepsCompleted() && (
        <Paper 
          square 
          elevation={0} 
          sx={{ 
            p: 3, 
            mt: 3, 
            background: 'rgba(0, 204, 153, 0.1)',
            borderRadius: theme.spacing(1),
            border: '1px solid rgba(0, 204, 153, 0.3)',
          }}
        >
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
            <Typography variant="h6" sx={{ color: '#00CC99' }}>
              Grand Total
            </Typography>
            <Typography variant="h5" sx={{ color: '#00CC99', fontWeight: 600 }}>
              ${calculateTotalCost().toLocaleString()}
            </Typography>
          </Box>
          
          <Typography variant="body2" sx={{ color: 'rgba(255, 255, 255, 0.7)', mb: 2 }}>
            Edit items above to adjust the total cost of your proposal.
          </Typography>
          
          <ResetButton onClick={handleReset} variant="outlined" sx={{ mt: 1 }} startIcon={<Refresh />}>
            Reset
          </ResetButton>
        </Paper>
      )}
    </StepperContainer>
  );
};

export default HolographicStepper; 